import {
  areFacilityNamesEquivalent,
  facilitiesMatchFlexible,
} from "./formatUtils";
/**
 * Creates a consolidated data structure from multiple data sources
 *
 * @param {Object} allInOneData - Main data object
 * @param {Object} facilitiesData - Facilities data object
 * @param {Object} hudDebtData - HUD debt data object
 * @param {Map} dealMap - Map of deal names to facility sets
 * @return {Object} Consolidated data structure
 */
export const createConsolidatedData = (
  allInOneData,
  facilitiesData,
  hudDebtData,
  dealMap
) => {
  const consolidatedData = {};

  dealMap.forEach((facilities, dealName) => {
    // Find the deal in facilitiesData to get Last Updated
    let lastUpdated = allInOneData[dealName]?.["Last Updated"];

    if (!lastUpdated && facilitiesData?.Deal) {
      const dealData = facilitiesData.Deal.find(d =>
        areFacilityNamesEquivalent(d.Deal, dealName)
      );
      if (dealData?.["Last Updated"]) {
        lastUpdated = dealData["Last Updated"];
      }
    }

    consolidatedData[dealName] = {
      facilities: {},
      lastUpdated: lastUpdated || null,
    };

    facilities.forEach((facilityName) => {
      const facilityData = {
        details: {},
        periods: {},
        hudDebt: {},
      };

      // Get facility details from facilities data
      if (facilitiesData?.Deal) {
        // Use areFacilityNamesEquivalent for deal matching too, not just facility names
        const dealData = facilitiesData.Deal.find((d) =>
          areFacilityNamesEquivalent(d.Deal, dealName)
        );

        if (dealData) {
          // Try to match facility by CCN first (most reliable), then by name
          let facilityDetails = null;

          // Get CCN from allInOneData if available
          const facilityPeriodData =
            allInOneData[dealName]?.Facilities[facilityName];
          const facilityCCN = facilityPeriodData?.CCN;

          // First attempt: Match by CCN (most reliable)
          if (facilityCCN) {
            facilityDetails = dealData?.Facilities.find(
              (f) => f.CCN && f.CCN === facilityCCN
            );
          }

          // Second attempt: Exact name match
          if (!facilityDetails) {
            facilityDetails = dealData?.Facilities.find((f) =>
              areFacilityNamesEquivalent(f.Facilities, facilityName)
            );
          }

          // Third attempt: Flexible name matching (handles "Bainbridge" vs "BAINBRIDGE HEALTH AND REHAB")
          if (!facilityDetails) {
            facilityDetails = dealData?.Facilities.find((f) =>
              facilitiesMatchFlexible(facilityName, f.Facilities)
            );
          }

          if (facilityDetails) {
            facilityData.details = { ...facilityDetails };
          } else {
            // Debug: Log when facility not found in AllFacilities.json
            console.warn(
              `⚠️ Facility "${facilityName}" not found in AllFacilities.json for deal "${dealName}"`
            );
          }
        } else {
          // Debug: Log when deal not found in AllFacilities.json
          console.warn(`⚠️ Deal "${dealName}" not found in AllFacilities.json`);
        }
      }

      // Get financial and operational data from allInOne
      if (allInOneData[dealName]?.Facilities[facilityName]?.Periods) {
        facilityData.periods =
          allInOneData[dealName].Facilities[facilityName].Periods;
      }

      // Get HUD debt data - try both with and without "Deal: " prefix
      if (hudDebtData[dealName]?.Facilities[facilityName]) {
        facilityData.hudDebt = hudDebtData[dealName].Facilities[facilityName];
      } else {
        // Try alternate naming conventions or transformations here if needed
        // Look for the dealName directly in hudDebtData
        Object.entries(hudDebtData).forEach(([hudDealKey, hudDealData]) => {
          if (areFacilityNamesEquivalent(hudDealKey, dealName)) {
            if (hudDealData.Facilities[facilityName]) {
              facilityData.hudDebt = hudDealData.Facilities[facilityName];
            }
          }
        });
      }

      consolidatedData[dealName].facilities[facilityName] = facilityData;
    });

    // If Consolidated is missing State, derive it from individual facilities.
    // Multi-state deals list all unique state codes, e.g. "OH, PA".
    const consolidated = consolidatedData[dealName].facilities["Consolidated"];
    if (consolidated && !consolidated.details?.State) {
      const states = [
        ...new Set(
          Object.entries(consolidatedData[dealName].facilities)
            .filter(([name]) => name !== "Consolidated")
            .map(([, fac]) => fac.details?.State)
            .filter(Boolean)
        ),
      ];
      if (states.length > 0) {
        consolidated.details = {
          ...consolidated.details,
          State: states.join(", "),
        };
      }
    }
  });

  return consolidatedData;
};
